import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Spinner, Button, Card, Badge, Modal } from 'react-bootstrap';
import axios from 'axios';
import { useCarrito } from './CarritoContext';
import { API } from '../../../../api/URL_API';
import { Cart2, Dash, Plus } from 'react-bootstrap-icons';
import FloatingButton from '../../FloatingButton';
import { trackViewContent, trackAddToCart } from '../../../../utils/metaPixel';

function ProductosUni({ idCategoria }) {
    const [products, setProducts] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [productsPerPage] = useState(12);
    const [totalProducts, setTotalProducts] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [selectedImage, setSelectedImage] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [addedProductId, setAddedProductId] = useState(null);

    const loaderRef = useRef(null);
    const { addToCarrito, selectedCategories } = useCarrito();

    useEffect(() => {
        window.scrollTo(0, 0);
        return () => {
            // Limpiar observador al desmontar
            if (loaderRef.current && observer.current) {
                observer.current.unobserve(loaderRef.current);
            }
        };
    }, []);

    const observer = useRef();

    const formatCurrency = (amount) => {
        const numberAmount = typeof amount === 'string'
            ? parseFloat(amount.replace(/[^0-9,-]+/g, ''))
            : amount;
        return numberAmount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    };

    const openImageModal = (product) => {
        setSelectedImage(product.imgProducto);
        setShowImageModal(true);
        trackViewContent(product);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
        setSelectedImage(null);
    };

    const fetchProducts = useCallback(async (page = 1, isInitialLoad = false) => {
        try {
            if (isInitialLoad) {
                setIsLoading(true);
                setProducts([]);
                setQuantities({});
                setHasMore(true); // Reiniciar paginación al inicio
            } else {
                setIsLoadingMore(true);
            }

            let url;
            let params = {
                limit: productsPerPage,
                offset: (page - 1) * productsPerPage,
                _: Date.now()
            };

            if (selectedCategories.length > 0) {
                url = `${API}/api/mostrarCategorias/productos-por-categorias`;
                params.categoryIds = selectedCategories.join(',');
            } else if (idCategoria) {
                url = `${API}/api/mostrarCategorias/productos/${idCategoria}`;
                params.page = page;
            }

            const response = await axios.get(url, { params, headers: { 'Cache-Control': 'no-cache' } });

            //console.log('Respuesta productos: ', page, response.data);

            if (response.data) {
                let productsData = [];
                let receivedTotal = 0;

                if (Array.isArray(response.data)) {
                    productsData = response.data;
                    receivedTotal = response.data.length;
                } else if (response.data.products && Array.isArray(response.data.products)) {
                    productsData = response.data.products;
                    receivedTotal = response.data.total || response.data.products.length;
                }

                const processedProducts = productsData.map(product => ({
                    ...product,
                    precioU: typeof product.precioU === 'string' ? parseFloat(product.precioU.replace(/[^0-9,-]+/g, '')) : product.precioU,
                    precioM: typeof product.precioM === 'string' ? parseFloat(product.precioM.replace(/[^0-9,-]+/g, '')) : product.precioM,
                }));

                setProducts(prev => {
                    const existingIds = new Set(prev.map(p => p.idProducto));
                    const newProducts = processedProducts.filter(p => !existingIds.has(p.idProducto));
                    const updatedProducts = [...prev, ...newProducts];

                    setHasMore(updatedProducts.length < receivedTotal);

                    return updatedProducts;
                });

                setTotalProducts(receivedTotal);

                setQuantities(prev => {
                    const newQuantities = { ...prev };
                    processedProducts.forEach(product => {
                        if (!newQuantities[product.idProducto]) {
                            newQuantities[product.idProducto] = 1;
                        }
                    });
                    return newQuantities;
                });

                // Si no recibimos productos nuevos, ya no hay más para cargar
                if (processedProducts.length === 0) {
                    setHasMore(false);
                }
            }
        } catch (error) {
            console.error('Error al cargar productos:', error);
            setHasMore(false);
        } finally {
            if (isInitialLoad) {
                setIsLoading(false);
            } else {
                setIsLoadingMore(false);
            }
        }
    }, [selectedCategories, idCategoria, productsPerPage]);


    useEffect(() => {
        // Resetear estado cuando cambian las categorías o idCategoria
        setCurrentPage(1);
        fetchProducts(1, true);
    }, [fetchProducts]);

    const handleAddToCart = (product) => {
        const quantityToCart = quantities[product.idProducto] || 1;

        addToCarrito({
            idProducto: product.idProducto,
            referencia: product.referencia,
            nomProducto: product.nombreOriginal || product.nomProducto,
            precioU: product.precioU,
            precioM: product.precioM,
            image: product.imgProducto || 'URL_DEFAULT_IMAGE',
            quantity: quantityToCart,
            stock: product.stock,
            // Categoría del producto (para promociones especiales por categoría).
            // El prop idCategoria es el nombre de la categoría de la ruta.
            nomCategoria: product.nomCategoria || product.categoria || idCategoria,
        });

        // Evento Meta Pixel AddToCart
        trackAddToCart(product, quantityToCart);

        // Feedback visual
        setAddedProductId(product.idProducto);

        setTimeout(() => {
            setAddedProductId(null);
        }, 1200);
    };

    const updateQuantity = (productId, newQuantity) => {
        setQuantities(prev => ({
            ...prev,
            [productId]: Math.max(1, Math.min(newQuantity, products.find(p => p.idProducto === productId)?.stock || 1))
        }));
    };

    // Configuración del IntersectionObserver
    useEffect(() => {
        if (!hasMore || isLoading || isLoadingMore) return;

        const options = {
            root: null,
            rootMargin: '100px',
            threshold: 0
        };

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setCurrentPage(prev => {
                    const nextPage = prev + 1;
                    fetchProducts(nextPage).then(() => {
                        // Después del fetch, si no hay más, no incrementamos el page
                        if (!hasMore) return prev;
                    });
                    return nextPage;
                });
            }
        }, options);

        if (loaderRef.current) {
            observer.current.observe(loaderRef.current);
        }

        return () => {
            if (observer.current) {
                observer.current.disconnect();
            }
        };
    }, [hasMore, isLoading, isLoadingMore, fetchProducts]);


    const visibleProducts = products.slice(0, currentPage * productsPerPage);
    const obtenerImagenesVisibles = () => visibleProducts.map(product => product.imgProducto);

    useEffect(() => {
        console.log(`Mostrando ${visibleProducts.length} de ${totalProducts} productos`);
    }, [visibleProducts, totalProducts]);

    return (
        <>
            <Container fluid className='py-4'>
                {/* Encabezado minimalista */}
                <div className='text-center mb-5'>
                    <h2 className='fw-bold text-uppercase mb-3' style={{ fontSize: '1.75rem', letterSpacing: '0.5px', color: '#2b235e' }}>
                        {idCategoria.replace(/-/g, " ")}
                    </h2>
                    <hr
                            style={{
                                width: "120px",
                                margin: "15px auto",
                                borderTop: "3px solid #dc3545",
                                opacity: 1
                            }}
                        />  
                </div>

                <Row className='g-3'>
                    {isLoading && !isLoadingMore ? (
                        <Col xs={12} className='text-center py-5'>
                            <Spinner animation='border' role='status' variant='danger' className='mb-3' />
                            <p className='text-dark fw-bold'>Cargando...</p>
                        </Col>
                    ) : visibleProducts.length === 0 ? (
                        <Col xs={12} className='text-center py-5'>
                            <p className='text-muted'>No hay productos disponibles.</p>
                        </Col>
                    ) : (
                        visibleProducts.map((product) => (
                            <Col key={product.idProducto} xs={6} sm={6} md={4} lg={3} className='d-flex'>
                                <Card className='w-100 border-1 shadow-sm' style={{ borderColor: '#e9ecef', position: 'relative' }}>
                                    {/* Badges de stock */}
                                    {product.stock <= 5 && product.stock > 0 && (
                                        <Badge
                                            className='position-absolute'
                                            style={{ top: '8px', right: '8px', zIndex: 10 }}
                                            bg='warning'
                                            text='dark'
                                        >
                                            Pocas unidades
                                        </Badge>
                                    )}
                                    {product.stock <= 0 && (
                                        <Badge
                                            className='position-absolute'
                                            style={{ top: '8px', right: '8px', zIndex: 10 }}
                                            bg='secondary'
                                        >
                                            Agotado
                                        </Badge>
                                    )}

                                    {/* Imagen */}
                                    <div
                                        className='bg-light d-flex align-items-center justify-content-center'
                                        style={{
                                            aspectRatio: '1',
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                                        }}
                                        onClick={() => openImageModal(product)}
                                        role='button'
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                openImageModal(product);
                                            }
                                        }}
                                        aria-label={`Ver imagen ampliada de ${product.nomProducto}`}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'scale(1.05)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'scale(1)';
                                        }}
                                    >
                                        <Card.Img
                                            variant='top'
                                            src={product.imgProducto}
                                            alt={product.nomProducto}
                                            loading='lazy'
                                            style={{ objectFit: 'contain', padding: '8px', height: '100%', pointerEvents: 'none' }}
                                        />
                                    </div>

                                    {/* Contenido */}
                                    <Card.Body className='p-3 d-flex flex-column'>
                                        {/* Nombre del producto */}
                                        <Card.Title
                                            className='mb-3 text1-cr'
                                            style={{
                                                fontSize: '1 rem',
                                                fontWeight: 600,
                                                lineHeight: 1.3,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical', 
                                                color: '#2b235e'
                                            }}
                                        >
                                            {product.nombreOriginal === null
                                                ? product.nomProducto
                                                : product.nombreOriginal}
                                        </Card.Title>

                                        {/* Caja de precios optimizada */}
                                        <div
                                            className='bg-light rounded p-2 mb-2'
                                            style={{
                                                border: '1px solid #ececec'
                                            }}
                                        >
                                            {/* Precio Unitario */}
                                            <div className='d-flex justify-content-between align-items-center mb-1'>
                                                <span
                                                    className='fw-bold text-uppercase text-muted'
                                                    style={{
                                                        fontSize: '0.65rem',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Unitario
                                                </span>

                                                <span
                                                    className='fw-bold text-nowrap'
                                                    style={{
                                                        fontSize: '0.95rem',
                                                        color: '#2b235e'
                                                    }}
                                                >
                                                    ${formatCurrency(product.precioU)}
                                                </span>
                                            </div>

                                            {/* Precio Mayorista */}
                                            <div className='d-flex justify-content-between align-items-center'>
                                                <span
                                                    className='fw-bold text-uppercase text-muted'
                                                    style={{
                                                        fontSize: '0.65rem',
                                                        letterSpacing: '0.5px'
                                                    }}
                                                >
                                                    Mayor
                                                </span>

                                                <span
                                                    className='fw-bold text-nowrap'
                                                    style={{
                                                        fontSize: '0.95rem',
                                                        color: '#dc3545'
                                                    }}
                                                >
                                                    ${formatCurrency(product.precioM)}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Controles de cantidad */}
                                        <div className='bg-light p-2 rounded mb-3 d-flex gap-2'>
                                            <Button
                                                size='sm'
                                                variant='outline-primary'
                                                onClick={() => updateQuantity(product.idProducto, quantities[product.idProducto] - 1)}
                                                disabled={quantities[product.idProducto] <= 1}
                                                className='p-0'
                                                style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 28px' }}
                                            >
                                                <Dash size={16} />
                                            </Button>
                                            <input
                                                type='number'
                                                min="1"
                                                max={product.stock}
                                                value={quantities[product.idProducto] || 1}
                                                onChange={(e) => {
                                                    let newQuantity = parseInt(e.target.value, 10) || 1;
                                                    newQuantity = Math.max(1, Math.min(newQuantity, product.stock));
                                                    updateQuantity(product.idProducto, newQuantity);
                                                }}
                                                disabled={product.stock <= 0}
                                                className='form-control form-control-sm text-center'
                                                style={{ flex: 1, fontWeight: 600, fontSize: '0.85rem' }}
                                            />
                                            <Button
                                                size='sm'
                                                variant='outline-success'
                                                onClick={() => updateQuantity(product.idProducto, quantities[product.idProducto] + 1)}
                                                disabled={quantities[product.idProducto] >= product.stock}
                                                className='p-0'
                                                style={{ width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 28px' }}
                                            >
                                                <Plus size={16} />
                                            </Button>
                                        </div>

                                        {/* Botón de compra */}
                                        <Button
                                            onClick={() => handleAddToCart(product)}
                                            disabled={product.stock <= 0}
                                            variant='dark'
                                            size='sm'
                                            className='w-100 fw-bold'
                                            style={{
                                                borderColor: '#2b235e',
                                                fontSize: '0.9rem',
                                                width: 'clamp(40px, 9vw, 48px)',
                                                height: 'clamp(40px, 9vw, 48px)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                backgroundColor:
                                                    addedProductId === product.idProducto
                                                        ? '#198754'
                                                        : '#2b235e',
                                                transform:
                                                    addedProductId === product.idProducto
                                                        ? 'scale(1.15)'
                                                        : 'scale(1)',
                                                transition: 'all 0.25s ease',
                                                opacity: product.stock <= 0 ? 0.5 : 1,
                                                flexShrink: 0
                                            }}
                                        >
                                            {addedProductId === product.idProducto ? (
                                                <span style={{ color: 'white', fontSize: '1rem' }}>
                                                    ✓
                                                </span>
                                            ) : (
                                                <>
                                                    <span className='d-sm-none'><Cart2 size={16} className='me-2' />Comprar</span>
                                                    <span className='d-none d-sm-block'><Cart2 size={20} className='me-2' />Comprar</span>
                                                </>
                                            )}
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))
                    )}
                </Row>

                {/* Loader */}
                {isLoadingMore && (
                    <Row className='mt-4'>
                        <Col xs={12} className='text-center py-3'>
                            <Spinner animation="border" size="sm" variant='danger' />
                        </Col>
                    </Row>
                )}

                <div ref={loaderRef} style={{ height: '20px' }} />
            </Container>
            <FloatingButton
                obtenerImagenesVisibles={obtenerImagenesVisibles}
                nomCategoria={idCategoria}
            />

            {/* Modal para ver imagen ampliada */}
            <Modal
                show={showImageModal}
                onHide={closeImageModal}
                centered
                contentClassName='bg-transparent border-0 shadow-none'
                backdropClassName='bg-dark'
            >
                <Modal.Body
                    className='p-0 d-flex justify-content-center align-items-center'
                    style={{
                        background: 'transparent'
                    }}
                >
                    {selectedImage && (
                        <div
                            className='position-relative d-inline-block'
                            style={{
                                maxWidth: '95vw',
                                maxHeight: '95vh'
                            }}
                        >
                            <img
                                src={selectedImage}
                                alt='Imagen ampliada'
                                style={{
                                    width: 'auto',
                                    height: 'auto',
                                    maxWidth: '95vw',
                                    maxHeight: '95vh',
                                    objectFit: 'contain',
                                    borderRadius: '12px',
                                    display: 'block'
                                }}
                            />

                            <Button
                                variant='dark'
                                onClick={closeImageModal}
                                className='position-absolute d-flex align-items-center justify-content-center p-0 border-0'
                                style={{
                                    top: '10px',
                                    right: '10px',
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: '50%',
                                    zIndex: 10,
                                    fontSize: '1.2rem',
                                    lineHeight: 1,
                                    background: 'rgba(0,0,0,0.7)',
                                    backdropFilter: 'blur(4px)'
                                }}
                            >
                                ✕
                            </Button>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </>
    );
}

export default ProductosUni;